import type { Block, Copy, Line, Span } from '@alphadesk/web/contract.ts';
import clsx from 'clsx';
import { ExternalLink, TriangleAlert } from 'lucide-react';
import { linkClass } from './primitives/controls.tsx';
import { EYEBROW, Eyebrow } from './primitives/shell.tsx';

const TONE_INK = { up: 'text-up', down: 'text-down' } as const;

function Said({ span }: { span: Span }) {
  if (typeof span === 'string') return span;
  if ('b' in span) return <strong className="font-semibold text-ink">{span.b}</strong>;
  if ('n' in span)
    return (
      <span className={clsx('tnum font-medium', span.tone ? TONE_INK[span.tone] : 'text-ink')}>
        {span.n}
      </span>
    );
  return <span className="font-mono text-ink">{span.c}</span>;
}

export const Words = ({ line }: { line: Line }) =>
  // biome-ignore lint/suspicious/noArrayIndexKey: a written note never reorders
  line.map((span, i) => <Said key={i} span={span} />);

function NoteBlock({ block }: { block: Block }) {
  if ('p' in block)
    return (
      <p>
        <Words line={block.p} />
      </p>
    );
  if ('facts' in block)
    return (
      <dl className="flex flex-wrap gap-x-4 gap-y-1.5">
        {block.facts.map(([label, value]) => (
          <div key={label} className="flex flex-col gap-0.5">
            <Eyebrow as="dt">{label}</Eyebrow>
            <dd className="leading-none">
              <Said span={value} />
            </dd>
          </div>
        ))}
      </dl>
    );
  if ('table' in block) {
    const { caption, head, rows, foot } = block.table;
    return (
      <div className="flex flex-col gap-1">
        {caption && <Eyebrow as="p">{caption}</Eyebrow>}
        <table className="w-full max-w-sm border-collapse">
          {head && (
            <thead>
              <tr className={EYEBROW}>
                <td />
                {head.map((one) => (
                  <th key={one} scope="col" className="pl-3 text-right font-normal">
                    {one}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map(({ label, cells }) => (
              <tr key={label}>
                <th scope="row" className="text-left font-normal">
                  {label}
                </th>
                {cells.map((cell, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: a written note never reorders
                  <td key={i} className="pl-3 text-right">
                    <Said span={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {foot && <p className="text-ink-faint">{foot}</p>}
      </div>
    );
  }
  if ('list' in block)
    return (
      <ul className="flex list-disc flex-col gap-0.5 pl-4 marker:text-ink-faint">
        {block.list.map((item, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: a written note never reorders
          <li key={i}>
            <Words line={item} />
          </li>
        ))}
      </ul>
    );
  if ('warn' in block)
    return (
      <p className="flex gap-1.5 rounded-control border border-warn/40 bg-warn/10 px-2 py-1.5 text-ink">
        <TriangleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0 text-warn" />
        <span>
          <Words line={block.warn} />
        </span>
      </p>
    );
  return (
    <figure className="flex flex-col gap-1">
      {block.quote !== '' && (
        <blockquote className="line-clamp-6 border-l-2 border-line pl-2.5 whitespace-pre-wrap italic">
          {block.quote}
        </blockquote>
      )}
      {block.href?.startsWith('https://') && (
        <a
          href={block.href}
          target="_blank"
          rel="noreferrer"
          className={linkClass(
            'quiet',
            `inline-flex items-center gap-1 self-start text-ink-faint ${block.quote === '' ? '' : 'pl-3'}`,
          )}
        >
          {block.label ?? 'See the post'}
          <ExternalLink aria-hidden className="size-3" />
        </a>
      )}
    </figure>
  );
}

/** What a folded note keeps: a glance needs the news, its figures and any warning; the rail adds the prose. */
const KEPT = {
  glance: (block: Block) => 'facts' in block || 'warn' in block,
  rail: (block: Block) => !('list' in block || 'quote' in block),
} as const;

export function NoteCopy({ copy, fold }: { copy: Copy; fold?: keyof typeof KEPT }) {
  const more = fold === undefined ? copy.more : copy.more.filter(KEPT[fold]);
  return (
    <div className="flex flex-col gap-2 text-xs leading-relaxed text-ink-muted">
      <p className="text-ink">
        <Words line={copy.lead} />
      </p>
      {more.map((block, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a written note never reorders
        <NoteBlock key={i} block={block} />
      ))}
    </div>
  );
}
